import { getTimeHorizonsForDuration } from '@/utils/timeHorizonGenerator';

export interface AutoDiscoveryConfigValue {
    byDuration: Record<string, number[]>;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const parsePositiveIntegerArray = (value: unknown): number[] | null => {
    if (!Array.isArray(value)) return null;

    const horizons = value.filter((horizon): horizon is number => typeof horizon === 'number' && Number.isInteger(horizon) && horizon > 0);

    return horizons.length === value.length && horizons.length > 0 ? horizons : null;
};

export const parseStoredAutoDiscoveryConfig = (
    rawConfig: string | null | undefined,
    durations: readonly string[],
    enabled: boolean
): AutoDiscoveryConfigValue | null => {
    if (!enabled) return null;

    try {
        const parsed: unknown = rawConfig ? JSON.parse(rawConfig) : null;

        if (isRecord(parsed) && isRecord(parsed.autoDiscovery) && isRecord(parsed.autoDiscovery.byDuration)) {
            const byDurationEntries = Object.entries(parsed.autoDiscovery.byDuration).flatMap(([duration, rawHorizons]) => {
                const horizons = parsePositiveIntegerArray(rawHorizons);
                if (!horizons) return [];

                // Legacy configs can hold pairs the menu has since retired (e.g. 60m-1800,
                // degenerate because a horizon shorter than the entry bar resolves back to
                // the entry bar). The grid cannot render them and the backend no longer
                // materializes them, so keeping them would re-save unsupported pairs.
                const menuHorizons = new Set(getTimeHorizonsForDuration(duration));
                const supportedHorizons = horizons.filter((horizon) => menuHorizons.has(horizon));

                return supportedHorizons.length > 0 ? ([[duration, supportedHorizons]] as const) : [];
            });

            if (byDurationEntries.length > 0) {
                return { byDuration: Object.fromEntries(byDurationEntries) };
            }
        }
    } catch {
        // Fall through to the all-pairs equivalent used by legacy enabled experiments.
    }

    return {
        byDuration: Object.fromEntries(durations.map((duration) => [duration, getTimeHorizonsForDuration(duration)]))
    };
};

export const parseStoredTimeHorizons = (rawConfig: string | null | undefined): number[] | null => {
    if (!rawConfig) return null;

    try {
        const parsed: unknown = JSON.parse(rawConfig);
        return isRecord(parsed) ? parsePositiveIntegerArray(parsed.timeHorizons) : null;
    } catch {
        return null;
    }
};

export const serializeExperimentHorizonConfig = (
    storedTimeHorizons: readonly number[] | null,
    autoDiscoveryConfig: AutoDiscoveryConfigValue | null
): string | null => {
    if (!storedTimeHorizons && !autoDiscoveryConfig) return null;

    return JSON.stringify({
        ...(storedTimeHorizons ? { timeHorizons: storedTimeHorizons } : {}),
        ...(autoDiscoveryConfig ? { autoDiscovery: autoDiscoveryConfig } : {})
    });
};
